import { useCallback, useEffect, useRef, useState } from "react";
import { Transition } from "react-transition-group";
import Button from "../Button";
import { ReactComponent as CrossSVG } from "assets/icons/cross.svg";
import { TransitionProps } from "react-transition-group/Transition";
import Spinner from "../Spinner";

const lockBodyScroll = () => {
  document.body.style.overflow = "hidden";
};
const unlockBodyScroll = () => {
  document.body.style.overflow = "auto";
};

/**
 * `$transitionShortMedium` in,
 * `$transitionShort` out
 */
const TIMEOUTS: TransitionProps["timeout"] = {
  enter: 0,
  appear: 450,
  exit: 300,
};

type ModalProps = {
  open: boolean;
  closeModal: VoidFunction;
  btnsDisabled?: boolean;
  closeDisabled?: boolean;
  showSpinner?: boolean;
  actionButton?: React.ReactNode;
  hideFooterButtons?: boolean;
  title: string;
  fullSize?: boolean;
  invertColours?: boolean;
  paddingClass?: "ReducedPadding" | "NoPadding";
  underlayClass?: "FaintUnderlay" | "ClearUnderlay" | "NoUnderlay";
  debugMode?: boolean;
};

const Modal: React.FC<React.PropsWithChildren<ModalProps>> = ({
  children,
  open,
  closeModal,
  title,
  btnsDisabled = false,
  closeDisabled = false,
  showSpinner = false,
  actionButton,
  hideFooterButtons = false,
  fullSize = false,
  invertColours = false,
  paddingClass = "",
  underlayClass = "",
  debugMode = false,
}) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const [isIn, setIsIn] = useState<boolean>(false);

  // Wrapper for Modal enter animation timing
  useEffect(() => {
    setTimeout(() => {
      if (open) {
        setIsIn(true);
      }
    }, TIMEOUTS.enter);
  }, [open]);

  // Wrapper for closeModal to account for animation timing
  const closeModalInner = useCallback(() => {
    if (!btnsDisabled) {
      setIsIn(false);
      setTimeout(() => {
        closeModal();
      }, TIMEOUTS.exit);
    }
  }, [btnsDisabled, closeModal]);

  // useEffect for when the open prop changes to `false`
  // i.e. If the external state forces the modal to close
  useEffect(() => {
    if (!open) {
      closeModalInner();
    }
  }, [open, closeModalInner]);

  return (
    <Transition
      nodeRef={nodeRef}
      in={isIn}
      timeout={TIMEOUTS}
      onEntered={lockBodyScroll}
      onExited={unlockBodyScroll}
      mountOnEnter
      unmountOnExit
    >
      {(transitionState) => (
        <>
          {/* Underlay */}
          <div
            className={`SharedUnderlay ${underlayClass} ${transitionState}`}
            onClick={!closeDisabled ? closeModalInner : undefined}
          >
            {/* Content */}
          </div>
          <div
            ref={nodeRef}
            className={`Modal ${fullSize ? "FullSize" : ""} ${
              invertColours ? "InvertColours" : ""
            } ${transitionState}`}
          >
            <div className={`Inner ${hideFooterButtons ? "NoFooter" : ""}`}>
              {/* Modal Header START */}
              <div className="Top">
                <h2 className="Title">{title}</h2>
                <Button
                  additionalClassNames="Cross NoBorder NoBackground"
                  onClick={closeModalInner}
                  disabled={closeDisabled}
                  children={
                    <CrossSVG
                      height={16}
                      width={16}
                      className={
                        invertColours ? "SVGFillWhite" : "SVGFillBlack"
                      }
                      role="img"
                      aria-label="Close Modal"
                      title="Close Modal"
                    />
                  }
                />
              </div>
              {/* Modal Header END */}
              {/* Modal Content START */}
              <div className={`Content ${paddingClass}`}>{children}</div>
              {/* Modal Content END */}
              {/* Modal Footer START */}
              {!hideFooterButtons && (
                <div className="DisplayFlex LargeGap Buttons">
                  <Button
                    colour="White"
                    additionalClassNames="PrimaryButton Large"
                    onClick={closeModalInner}
                    disabled={btnsDisabled}
                  >
                    Cancel
                  </Button>
                  {actionButton}
                </div>
              )}
              {/* Modal Footer END */}
            </div>
            <Spinner show={showSpinner} underlayClass="FaintUnderlay" />
          </div>
        </>
      )}
    </Transition>
  );
};

export default Modal;
